import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal } from '../common/Modal'
import { Button } from '../common/Button'
import api from '../../services/api'
import {
  buildKanaSuggestions,
  normalizeWordForTranslation,
  type KanaSuggestion,
} from '../../utils/japaneseText'
import { toNumericUserId } from '../../utils/userId'
import { useUserStore } from '../../store/userStore'
import { useLanguageStore } from '../../store/languageStore'

interface FlashcardCreateModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  userId: string
}

type CandidateStatus = 'idle' | 'loading' | 'ready' | 'error'

interface GeneratedFlashcardData {
  targetText: string
  pronunciation: string
  sourceTranslation: string
  exampleSentence: string
  exampleTranslation: string
}

interface CandidateCard extends KanaSuggestion {
  status: CandidateStatus
  data?: GeneratedFlashcardData
  error?: string
}

const EMPTY_FORM: GeneratedFlashcardData = {
  targetText: '',
  pronunciation: '',
  sourceTranslation: '',
  exampleSentence: '',
  exampleTranslation: '',
}

const MAX_SUGGESTIONS = 5
const MANUAL_SESSION_ID = 'manual-flashcard'
const AUDIO_FETCH_TIMEOUT_MS = 5000

export function FlashcardCreateModal({
  isOpen,
  onClose,
  onSuccess,
  userId,
}: FlashcardCreateModalProps) {
  const navigate = useNavigate()
  const user = useUserStore((state) => state.user)
  const currentLanguageCode = useLanguageStore((state) => state.currentLanguage)
  const availableLanguages = useLanguageStore((state) => state.availableLanguages)
  const currentLanguage = availableLanguages.find((lang) => lang.code === currentLanguageCode)
  const [query, setQuery] = useState('')
  const [candidateCards, setCandidateCards] = useState<CandidateCard[]>([])
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(null)
  const [formData, setFormData] = useState<GeneratedFlashcardData>(EMPTY_FORM)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const translationCacheRef = useRef<Record<string, GeneratedFlashcardData>>({})
  const canonicalCacheRef = useRef<Record<string, GeneratedFlashcardData>>({})
  const loadingCanonicalsRef = useRef<Set<string>>(new Set())

  const resetState = () => {
    setQuery('')
    setCandidateCards([])
    setSelectedCandidate(null)
    setFormData(EMPTY_FORM)
    translationCacheRef.current = {}
    canonicalCacheRef.current = {}
    loadingCanonicalsRef.current.clear()
    setError(null)
  }

  useEffect(() => {
    if (!isOpen) {
      resetState()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const trimmed = query.trim()
    if (!trimmed) {
      setCandidateCards([])
      setSelectedCandidate(null)
      return
    }

    // Only use kana suggestions for Japanese, otherwise use plain text
    let candidateSuggestions: KanaSuggestion[]
    if (currentLanguage?.code === 'ja') {
      const rawSuggestions = buildKanaSuggestions(trimmed)
      candidateSuggestions = rawSuggestions.filter((suggestion) => suggestion.type !== 'romaji')
      if (candidateSuggestions.length === 0) {
        candidateSuggestions = [{ text: trimmed, type: 'hiragana' as const }]
      }
    } else {
      // For other languages, just use the query as-is
      candidateSuggestions = [{ text: trimmed, type: 'hiragana' as const }]
    }
    const limited = candidateSuggestions.slice(0, MAX_SUGGESTIONS)

    setCandidateCards((prev) => {
      const prevMap = new Map(prev.map((card) => [card.text, card]))
      return limited.map((suggestion) => {
        const existing = prevMap.get(suggestion.text)
        if (existing) {
          return {
            ...existing,
            autoCorrectedFrom: suggestion.autoCorrectedFrom,
            type: suggestion.type,
          }
        }
        const cached = translationCacheRef.current[suggestion.text]
        return {
          ...suggestion,
          status: cached ? 'ready' : 'idle',
          data: cached,
          error: undefined,
        }
      })
    })

    setSelectedCandidate((prevSelected) =>
      limited.some((suggestion) => suggestion.text === prevSelected) ? prevSelected : null
    )
  }, [query, isOpen, currentLanguage])

  useEffect(() => {
    if (!isOpen) return
    if (!userId) return

    const pending = candidateCards.filter(
      (card) => card.status === 'idle' && card.text.trim().length > 0
    )
    if (pending.length === 0) return

    const immediateUpdates: Record<string, GeneratedFlashcardData> = {}
    const pendingByCanonical = new Map<string, CandidateCard[]>()

    pending.forEach((card) => {
      const canonical = normalizeWordForTranslation(card.text)
      const cached = canonicalCacheRef.current[canonical]
      if (cached) {
        translationCacheRef.current[card.text] = cached
        immediateUpdates[card.text] = cached
        return
      }
      if (!pendingByCanonical.has(canonical)) {
        pendingByCanonical.set(canonical, [])
      }
      pendingByCanonical.get(canonical)!.push(card)
    })

    if (Object.keys(immediateUpdates).length > 0) {
      setCandidateCards((prev) =>
        prev.map((card) =>
          immediateUpdates[card.text]
            ? { ...card, status: 'ready', data: immediateUpdates[card.text], error: undefined }
            : card
        )
      )
    }

    const entriesToFetch = Array.from(pendingByCanonical.entries()).filter(
      ([canonical]) => !loadingCanonicalsRef.current.has(canonical)
    )
    if (entriesToFetch.length === 0) return

    const loadingTexts = new Set<string>()
    entriesToFetch.forEach(([, cards]) => {
      cards.forEach((card) => loadingTexts.add(card.text))
    })

    if (loadingTexts.size > 0) {
      setCandidateCards((prev) =>
        prev.map((card) =>
          loadingTexts.has(card.text) ? { ...card, status: 'loading', error: undefined } : card
        )
      )
    }

    entriesToFetch.forEach(([canonical, cards]) => {
      loadingCanonicalsRef.current.add(canonical)
      const sourceText = cards[0].text

      ;(async () => {
        try {
          const response = await api.translateWordAPI({
            word: sourceText,
            userId,
            sessionId: MANUAL_SESSION_ID,
            targetLanguage: currentLanguageCode || 'ja',
            skipFlashcardCreate: true,
          })

          const generated: GeneratedFlashcardData = {
            targetText: response.word || sourceText,
            pronunciation: response.furigana || '',
            sourceTranslation: response.translation || '',
            exampleSentence: response.exampleSentence || '',
            exampleTranslation: response.exampleTranslation || '',
          }

          canonicalCacheRef.current[canonical] = generated
          cards.forEach((card) => {
            translationCacheRef.current[card.text] = generated
          })

          setCandidateCards((prev) =>
            prev.map((card) =>
              cards.some((c) => c.text === card.text)
                ? { ...card, status: 'ready', data: generated, error: undefined }
                : card
            )
          )
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Translation failed'
          setCandidateCards((prev) =>
            prev.map((card) =>
              cards.some((c) => c.text === card.text)
                ? { ...card, status: 'error', error: message }
                : card
            )
          )
        } finally {
          loadingCanonicalsRef.current.delete(canonical)
        }
      })()
    })
  }, [candidateCards, userId, isOpen])

  const handleSuggestionSelect = (card: CandidateCard) => {
    if (card.status !== 'ready' || !card.data) return
    setSelectedCandidate(card.text)
    setFormData({
      targetText: card.data.targetText,
      pronunciation: card.data.pronunciation,
      sourceTranslation: card.data.sourceTranslation,
      exampleSentence: card.data.exampleSentence,
      exampleTranslation: card.data.exampleTranslation,
    })
    setError(null)
  }

  const handleFieldChange =
    (field: keyof GeneratedFlashcardData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }))
      if (error) setError(null)
    }

  const generateAudioForManualCard = async (text: string): Promise<string | null> => {
    const trimmed = text.trim()
    if (!trimmed) return null

    try {
      const tier = user?.subscriptionTier === 'premium' ? 'elevenlabs' : 'google'
      const preferredVoice =
        tier === 'elevenlabs' ? user?.preferredVoiceId || undefined : undefined
      const speechPromise = api.speakJapaneseText(trimmed, tier, preferredVoice, currentLanguage?.code)
      const spoken = await new Promise<any>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('audio-timeout')), AUDIO_FETCH_TIMEOUT_MS)
        speechPromise.then(
          (result) => {
            clearTimeout(timer)
            resolve(result)
          },
          (error) => {
            clearTimeout(timer)
            reject(error)
          }
        )
      })
      return spoken?.audioUrl || null
    } catch (err) {
      console.warn('[FlashcardCreateModal] Failed to fetch audio, continuing without it', err)
      return null
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const missingField = Object.entries(formData).find(([_, value]) => !value.trim())
    if (missingField) {
      setError('Please select a suggestion or fill in all fields before creating the flashcard.')
      return
    }

    setIsSubmitting(true)

    try {
      const { getFirestore, collection, addDoc } = await import('firebase/firestore')
      const db = getFirestore()

      const today = new Date().toISOString().split('T')[0]
      const numericUserId = toNumericUserId(userId)
      const audioUrl = await generateAudioForManualCard(formData.targetText)
      const languageCode = currentLanguage?.code || 'ja'

      await addDoc(collection(db, 'flashcards'), {
        numId: Date.now() % 999999999,
        userId: numericUserId,
        targetLanguage: languageCode,
        targetText: formData.targetText,
        pronunciation: formData.pronunciation,
        sourceText: formData.sourceTranslation,
        // Backward compatibility fields for Japanese
        japaneseText: languageCode === 'ja' ? formData.targetText : undefined,
        furigana: languageCode === 'ja' ? formData.pronunciation : undefined,
        englishTranslation: formData.sourceTranslation,
        exampleSentence: formData.exampleSentence,
        exampleTranslation: formData.exampleTranslation,
        sourceSessionId: null,
        audioUrl,
        masteryLevel: 'new',
        easeFactor: 2.5,
        intervalDays: 0,
        repetitions: 0,
        totalReviews: 0,
        correctReviews: 0,
        accuracy: 0,
        nextReviewDate: today,
        lastReviewDate: null,
        createdAt: new Date().toISOString(),
      })

      resetState()
      onClose()

      // Wait a moment for Firestore to propagate, then trigger refetch
      setTimeout(() => {
        onSuccess()
      }, 500)
    } catch (err: any) {
      console.error('Flashcard creation error:', err)
      setError(err?.message || 'Failed to create flashcard. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (isSubmitting) return
    resetState()
    onClose()
  }

  const isCreateDisabled =
    isSubmitting || Object.values(formData).some((value) => !value.trim().length)

  const renderCandidateStatus = (card: CandidateCard) => {
    if (card.status === 'loading') {
      return <span className="text-xs text-gray-500">Generating...</span>
    }
    if (card.status === 'error') {
      return <span className="text-xs text-error-600">{card.error || 'Failed to translate'}</span>
    }
    if (card.status === 'ready') {
      return (
        <span className="text-xs font-medium text-primary-700">
          {selectedCandidate === card.text ? 'Selected' : 'Tap to use'}
        </span>
      )
    }
    return <span className="text-xs text-gray-400">Preparing...</span>
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Flashcard"
      footer={
        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isCreateDisabled}>
            {isSubmitting ? 'Creating...' : 'Create Flashcard'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-error-50 border border-error-200 px-4 py-3 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-error-700 text-sm">{error}</span>
              {(error.includes('limit') || error.includes('Premium')) && (
                <Button
                  variant="primary"
                  onClick={() => {
                    handleClose()
                    navigate('/pricing')
                  }}
                  className="text-sm ml-4"
                >
                  Upgrade
                </Button>
              )}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {currentLanguage?.code === 'ja'
              ? 'Type a word or phrase (romaji or Japanese)'
              : `Type a word or phrase in ${currentLanguage?.name || 'the target language'}`}
          </label>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              if (error) setError(null)
            }}
            placeholder={
              currentLanguage?.code === 'ja'
                ? 'e.g., konnichiwa'
                : currentLanguage?.code === 'es'
                ? 'e.g., hola'
                : currentLanguage?.code === 'tl'
                ? 'e.g., kumusta'
                : 'e.g., hello'
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={isSubmitting}
          />
          {currentLanguage?.code === 'ja' ? (
            <p className="text-xs text-gray-500 mt-1">
              We will auto-correct romaji like &ldquo;konichiwa&rdquo;, show multiple readings, and
              pre-fill the card for you.
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">
              Enter a word or phrase and we'll generate translation and example for you.
            </p>
          )}
        </div>

        {candidateCards.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Suggestions
            </p>
            <div className="space-y-2">
              {candidateCards.map((card) => (
                <button
                  key={card.text}
                  type="button"
                  onClick={() => handleSuggestionSelect(card)}
                  disabled={card.status !== 'ready'}
                  className={`w-full text-left border rounded-lg px-4 py-3 transition ${
                    selectedCandidate === card.text
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 bg-white hover:border-primary-300'
                  } ${card.status !== 'ready' ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-lg font-semibold text-gray-900">
                        {card.data?.targetText || card.text}
                      </p>
                      {card.autoCorrectedFrom && (
                        <p className="text-xs text-gray-500">
                          Auto-corrected from "{card.autoCorrectedFrom}"
                        </p>
                      )}
                    </div>
                    {renderCandidateStatus(card)}
                  </div>
                  <p className="text-sm text-gray-700 mt-2">
                    {card.data?.sourceTranslation ||
                      (card.status === 'loading'
                        ? 'Generating translation...'
                        : card.error || 'Translation unavailable')}
                  </p>
                  {card.data?.exampleSentence && (
                    <div className="mt-2 text-xs text-gray-600 space-y-1">
                      <p className="font-medium">{card.data.exampleSentence}</p>
                      <p>{card.data.exampleTranslation}</p>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800">Flashcard Details</h3>
            {selectedCandidate ? (
              <span className="text-xs text-primary-600">
                Filled from &ldquo;{selectedCandidate}&rdquo;
              </span>
            ) : (
              <span className="text-xs text-gray-500">Select a suggestion or fill manually</span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {currentLanguage?.name || 'Target Language'} Text *
            </label>
            <input
              type="text"
              value={formData.targetText}
              onChange={handleFieldChange('targetText')}
              maxLength={100}
              placeholder={
                currentLanguage?.code === 'ja'
                  ? 'e.g., こんにちは'
                  : currentLanguage?.code === 'es'
                  ? 'e.g., hola'
                  : currentLanguage?.code === 'tl'
                  ? 'e.g., kumusta'
                  : 'e.g., hello'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {currentLanguage?.code === 'ja' ? 'Furigana' : 'Pronunciation'} *
            </label>
            <input
              type="text"
              value={formData.pronunciation}
              onChange={handleFieldChange('pronunciation')}
              maxLength={200}
              placeholder={
                currentLanguage?.code === 'ja'
                  ? 'e.g., こんにちは'
                  : currentLanguage?.code === 'es'
                  ? 'e.g., oh-lah'
                  : currentLanguage?.code === 'tl'
                  ? 'e.g., koo-moos-tah'
                  : 'e.g., pronunciation'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              English Translation *
            </label>
            <input
              type="text"
              value={formData.sourceTranslation}
              onChange={handleFieldChange('sourceTranslation')}
              maxLength={500}
              placeholder="e.g., hello"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Example Sentence ({currentLanguage?.name || 'Target Language'}) *
            </label>
            <textarea
              value={formData.exampleSentence}
              onChange={handleFieldChange('exampleSentence')}
              maxLength={500}
              rows={2}
              placeholder={
                currentLanguage?.code === 'ja'
                  ? 'e.g., 朝、隣の人にこんにちはと挨拶しました。'
                  : currentLanguage?.code === 'es'
                  ? 'e.g., Dije hola a mi vecino esta mañana.'
                  : currentLanguage?.code === 'tl'
                  ? 'e.g., Kumusta ang iyong araw?'
                  : 'e.g., Example sentence'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Example Translation *
            </label>
            <textarea
              value={formData.exampleTranslation}
              onChange={handleFieldChange('exampleTranslation')}
              maxLength={500}
              rows={2}
              placeholder="e.g., I greeted my neighbor with hello in the morning."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              disabled={isSubmitting}
            />
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default FlashcardCreateModal
